import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Input,
  message,
  Popconfirm,
  Radio,
  Select,
  Table,
  Tag,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { getCartoes, confirmarCartoes } from "@/services/cartao";
import { getUnidades } from "@/services/unidade";
import { CartaoType } from "@/types/CartaoType";
import { usePermissions } from "@/hooks/usePermissions";

dayjs.locale("pt-br");

const { RangePicker } = DatePicker;

const formatValor = (valor?: number) =>
  valor != null
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "-";

const formatData = (data?: string) => (data ? dayjs(data).format("DD/MM/YYYY") : "-");

type GrupoTransacao = {
  key: string;
  alunoId: number;
  matricula?: number;
  unidadeNome?: string;
  nomeAluno?: string;
  cv: number;
  dataPagamento?: string;
  dataPrimeiraParcela?: string;
  quantidade: number;
  valorTotal: number;
  parcelas: CartaoType[];
};

export default function ConsultaCartao() {
  const { canRecebimentoFinanceiro } = usePermissions();
  const [parcelas, setParcelas] = useState<CartaoType[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [unidadeIds, setUnidadeIds] = useState<number[]>([]);
  const [periodo, setPeriodo] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [busca, setBusca] = useState("");
  const [filtroConfirmacao, setFiltroConfirmacao] = useState<"todos" | "aConfirmar" | "confirmados">("aConfirmar");

  const carregarParcelas = useCallback(async () => {
    try {
      const filtro = {
        confirmado:
          filtroConfirmacao === "todos" ? undefined : filtroConfirmacao === "confirmados",
        unidadeIds: unidadeIds.length > 0 ? unidadeIds : undefined,
        dataInicio: periodo?.[0] ? periodo[0].format("YYYY-MM-DD") : undefined,
        dataFim: periodo?.[1] ? periodo[1].format("YYYY-MM-DD") : undefined,
        busca: busca.trim() || undefined,
      };
      const { data } = await getCartoes(filtro);
      setParcelas(data ?? []);
    } catch {
      message.error("Erro ao carregar os pagamentos em cartão.");
    }
  }, [filtroConfirmacao, unidadeIds, periodo, busca]);

  useEffect(() => {
    carregarParcelas();
  }, [carregarParcelas]);

  useEffect(() => {
    getUnidades()
      .then(({ data }) => setUnidades(data ?? []))
      .catch(() => message.error("Erro ao carregar unidades."));
  }, []);

  const executar = async (acao: () => Promise<any>, sucesso: string) => {
    try {
      await acao();
      message.success(sucesso);
      carregarParcelas();
    } catch (error: any) {
      message.error(error?.response?.data ?? "Erro ao executar a operação.");
    }
  };

  const gruposTransacao: GrupoTransacao[] = useMemo(() => {
    const mapa = new Map<string, GrupoTransacao>();
    parcelas.forEach((p) => {
      const chave = `${p.alunoId}-${p.cv}`;
      const grupo = mapa.get(chave) ?? {
        key: chave,
        alunoId: p.alunoId,
        matricula: p.matricula,
        unidadeNome: p.unidadeNome,
        nomeAluno: p.nomeAluno,
        cv: p.cv,
        dataPagamento: p.dataPagamento,
        dataPrimeiraParcela: p.dataPrimeiraParcela,
        quantidade: 0,
        valorTotal: 0,
        parcelas: [],
      };
      grupo.quantidade += 1;
      grupo.valorTotal += p.valor ?? 0;
      grupo.parcelas.push(p);
      mapa.set(chave, grupo);
    });
    return Array.from(mapa.values());
  }, [parcelas]);

  const renderStatus = (registro: CartaoType) =>
    registro.confirmado ? (
      <Tag color="green">Confirmado</Tag>
    ) : (
      <Tag color="default">À Confirmar</Tag>
    );

  const colunasGrupo: ColumnsType<GrupoTransacao> = [
    { title: "Matrícula", dataIndex: "matricula", key: "matricula" },
    { title: "Unidade", dataIndex: "unidadeNome", key: "unidadeNome" },
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    { title: "CV", dataIndex: "cv", key: "cv" },
    {
      title: "Data Pagamento",
      dataIndex: "dataPagamento",
      key: "dataPagamento",
      render: (v: string) => formatData(v),
    },
    {
      title: "Data 1ª Parcela",
      dataIndex: "dataPrimeiraParcela",
      key: "dataPrimeiraParcela",
      render: (v: string) => formatData(v),
    },
    { title: "Qtd. Parcelas", dataIndex: "quantidade", key: "quantidade" },
    {
      title: "Valor Total",
      dataIndex: "valorTotal",
      key: "valorTotal",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, grupo) => {
        const pendentes = grupo.parcelas.filter((p) => !p.confirmado);
        if (pendentes.length === 0) return <Tag color="green">Confirmados</Tag>;
        if (!canRecebimentoFinanceiro) return null;
        return (
          <Popconfirm
            title="Confirmar transação?"
            description={`Confirmar ${pendentes.length} parcela(s) do cartão (CV ${grupo.cv})?`}
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(
                () => confirmarCartoes(pendentes.map((p) => p.id)),
                "Confirmação registrada com sucesso!"
              )
            }
          >
            <Button type="primary" size="small">
              Confirmar
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const colunasParcelasDoGrupo: ColumnsType<CartaoType> = [
    {
      title: "Status",
      key: "status",
      render: (_, registro) => renderStatus(registro),
    },
    { title: "Parcela", dataIndex: "parcela", key: "parcela" },
    {
      title: "Data",
      dataIndex: "dataParcela",
      key: "dataParcela",
      render: (v: string) => formatData(v),
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Data Confirmação",
      dataIndex: "dataConfirmacao",
      key: "dataConfirmacao",
      render: (v: string) => formatData(v),
    },
    { title: "Confirmado por", dataIndex: "confirmadoPorNome", key: "confirmadoPorNome" },
    {
      title: "Ação",
      key: "acao",
      render: (_, registro) =>
        !registro.confirmado && canRecebimentoFinanceiro ? (
          <Popconfirm
            title="Confirmar esta parcela do cartão?"
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(() => confirmarCartoes([registro.id]), "Confirmação registrada com sucesso!")
            }
          >
            <Button type="link" size="small">
              Confirmar
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Controle de Cartão</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-center mb-6">
          <Input
            allowClear
            placeholder="Buscar por nome ou CPF"
            className="w-80"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select
            allowClear
            mode="multiple"
            placeholder="Todas as unidades"
            className="w-64"
            value={unidadeIds}
            onChange={(value) => setUnidadeIds(value)}
            options={unidades.map((u: any) => ({ value: u.id, label: u.nome }))}
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={periodo}
            onChange={(datas) => setPeriodo(datas as [Dayjs | null, Dayjs | null] | null)}
            placeholder={["Data inicial", "Data final"]}
          />
        </div>

        <div className="mb-4">
          <Radio.Group
            value={filtroConfirmacao}
            onChange={(e) => setFiltroConfirmacao(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: "⏳ À Confirmar", value: "aConfirmar" },
              { label: "✅ Confirmados", value: "confirmados" },
              { label: "Todos", value: "todos" },
            ]}
          />
        </div>

        <Table
          columns={colunasGrupo}
          dataSource={gruposTransacao}
          rowKey="key"
          expandable={{
            expandedRowRender: (grupo) => (
              <Table
                columns={colunasParcelasDoGrupo}
                dataSource={grupo.parcelas}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ),
          }}
        />
      </ConfigProvider>
    </>
  );
}
